import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Search parameters for hotel searches.
 */
export interface SearchParams {
  checkIn: string | null;
  checkOut: string | null;
  adults: number;
  children: number;
  rooms: number;
}

/**
 * Search session state for tracking Xeni correlation IDs and search parameters.
 *
 * Xeni uses correlation IDs to track user sessions from autocomplete through
 * search, property details, and booking. This context manages the correlation ID
 * so it's automatically included in all related API calls.
 *
 * It also stores search parameters (dates, guests) so they persist across
 * page navigations (search results → property details → booking).
 *
 * Flow:
 * 1. User types in autocomplete → Xeni returns correlation ID
 * 2. User selects destination → correlation ID stored in context
 * 3. Search, filters, sort, property details, booking → all use same correlation ID
 * 4. New search (different destination) → new correlation ID from autocomplete
 */
export interface SearchSession {
  /** Xeni correlation ID for the current search session */
  correlationId: string | null;
  /** Location data from the selected autocomplete result */
  location: {
    placeId: string | null;
    latitude: number | null;
    longitude: number | null;
    name: string | null;
  } | null;
  /** Search parameters (dates, guests) */
  searchParams: SearchParams;
}

export interface SearchSessionContextValue {
  session: SearchSession;
  /** Set correlation ID from autocomplete response */
  setCorrelationId: (id: string | null) => void;
  /** Set location data from autocomplete selection */
  setLocation: (location: SearchSession["location"]) => void;
  /** Set search parameters (dates, guests) */
  setSearchParams: (params: Partial<SearchParams>) => void;
  /** Start a new session (clears correlation ID and location) */
  startNewSession: () => void;
  /** Check if we have a valid session (has correlation ID) */
  hasSession: boolean;
}

const defaultSearchParams: SearchParams = {
  checkIn: null,
  checkOut: null,
  adults: 2,
  children: 0,
  rooms: 1,
};

const defaultSession: SearchSession = {
  correlationId: null,
  location: null,
  searchParams: defaultSearchParams,
};

const SearchSessionContext = createContext<SearchSessionContextValue | null>(null);

export const SearchSessionProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<SearchSession>(defaultSession);

  const setCorrelationId = useCallback((id: string | null) => {
    setSession((prev) => ({ ...prev, correlationId: id }));
  }, []);

  const setLocation = useCallback((location: SearchSession["location"]) => {
    setSession((prev) => ({ ...prev, location }));
  }, []);

  const setSearchParams = useCallback((params: Partial<SearchParams>) => {
    setSession((prev) => ({
      ...prev,
      searchParams: { ...prev.searchParams, ...params },
    }));
  }, []);

  const startNewSession = useCallback(() => {
    setSession(defaultSession);
  }, []);

  const value = useMemo<SearchSessionContextValue>(
    () => ({
      session,
      setCorrelationId,
      setLocation,
      setSearchParams,
      startNewSession,
      hasSession: session.correlationId !== null,
    }),
    [session, setCorrelationId, setLocation, setSearchParams, startNewSession]
  );

  return (
    <SearchSessionContext.Provider value={value}>
      {children}
    </SearchSessionContext.Provider>
  );
};

/**
 * Hook to access the search session context.
 * Must be used within a SearchSessionProvider.
 */
export const useSearchSession = (): SearchSessionContextValue => {
  const context = useContext(SearchSessionContext);
  if (!context) {
    throw new Error("useSearchSession must be used within a SearchSessionProvider");
  }
  return context;
};

/**
 * Optional hook that returns null if not within provider.
 * Useful for components that may or may not be in a search context.
 */
export const useSearchSessionOptional = (): SearchSessionContextValue | null => {
  return useContext(SearchSessionContext);
};

export default SearchSessionContext;
